import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import DashboardLayout from '@/components/layout/DashboardLayout';

interface SubscriptionRow {
  id: string;
  user_id: string;
  status: string;
  price_id?: string | null;
  created_at: string;
  updated_at: string;
}

export default function SubscriptionsTable() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-subscriptions', page, search],
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Basic subscription table; using posit_subscriptions if present
      let query = supabase
        .from('posit_subscriptions')
        .select('id, user_id, status, price_id, created_at, updated_at', { count: 'exact' })
        .order('updated_at', { ascending: false })
        .range(from, to);

      if (search) {
        // When searching, filter by user_id prefix for now
        query = query.ilike('user_id', `${search}%`);
      }

      const { data: rows, count } = await query as any;
      return { rows: (rows || []) as SubscriptionRow[], count: count || 0 };
    },
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data?.count || 0) / pageSize)), [data?.count]);

  return (
    <DashboardLayout title="Subscriptions" maxWidth="7xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 w-full justify-end">
          <Input placeholder="Search user id…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="w-64" />
        </div>
      </div>

        <div className="border rounded-lg">
          {isLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.rows || []).map((s) => (
                <TableRow key={s.id} className="cursor-pointer" onClick={() => navigate(`/admin/business/subscriptions/${s.id}`)}>
                  <TableCell className="font-mono text-xs">{s.id}</TableCell>
                  <TableCell className="font-mono text-xs">{s.user_id}</TableCell>
                  <TableCell>{s.status}</TableCell>
                  <TableCell className="font-mono text-xs">{s.price_id || '—'}</TableCell>
                  <TableCell>{new Date(s.updated_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {(!isLoading && (data?.rows || []).length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">No subscriptions found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
            <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
          </div>
        </div>
    </DashboardLayout>
  );
}


